import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders, getCurrentAdmin, logout } from '../components/ProtectedRoute';
import { 
  Plus,
  Play,
  Edit,
  Trash2,
  MoreVertical,
  Clock,
  Layers,
  Monitor,
  Users,
  Video,
  Presentation,
  CalendarDays,
  MapPin,
  User,
  UserPlus,
  Mail,
  Phone,
  Award,
  Upload,
  Camera,
  X,
  FileText,
  Loader2,
  ClipboardList,
  CheckCircle,
  XCircle,
  Building,
  Globe,
  Handshake,
  Image as ImageIcon,
  FileCheck,
  LogOut,
  Shield,
  Key,
  UserCog,
  Send,
  UserCheck,
  ShieldCheck,
  ShieldOff,
  Share2
} from 'lucide-react';
import ShareCourseDialog from '../components/ShareCourseDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRIMARY_ADMIN_EMAIL = "rob@closenowlending.com";

export default function DashboardPage() {
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduledCourses, setScheduledCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [courseRequests, setCourseRequests] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showInstructorDialog, setShowInstructorDialog] = useState(false);
  const [showSponsorAssignDialog, setShowSponsorAssignDialog] = useState(false);
  const [selectedScheduledCourse, setSelectedScheduledCourse] = useState(null);
  const [sponsorAssignments, setSponsorAssignments] = useState([]);
  const [courseTypeFilter, setCourseTypeFilter] = useState('all'); // all, ce, professional_development
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    course_id: '',
    instructor_id: '',
    delivery_format: 'classroom',
    timezone: 'America/Chicago',
    scheduled_date: '',
    scheduled_time: '18:00',
    venue_name: '',
    address_1: '',
    address_2: '',
    city: '',
    state: 'TX',
    zip: '',
    venue_website: '',
    attendee_limit: '',
    is_private: false,
    is_test_event: false,
    agreement_accepted: false,
    notes: '',
    location: ''
  });
  const [instructorForm, setInstructorForm] = useState({
    name: '',
    title: '',
    bio: '',
    email: '',
    phone: '',
    website: '',
    specialties: '',
    photo_url: '',
    certification_pdf_url: ''
  });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCertification, setUploadingCertification] = useState(false);
  
  // Invite New Instructor State
  const [showInviteInstructorDialog, setShowInviteInstructorDialog] = useState(false);
  const [inviteInstructorForm, setInviteInstructorForm] = useState({
    name: '',
    email: '',
    bio: '',
    phone: ''
  });
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Invite Sponsor State
  const [showInviteSponsorDialog, setShowInviteSponsorDialog] = useState(false);
  const [inviteSponsorForm, setInviteSponsorForm] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [sendingSponsorInvite, setSendingSponsorInvite] = useState(false);
  
  // Course Import State
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importForm, setImportForm] = useState({
    title: '',
    description: '',
    credit_hours: 1,
    category: 'General',
    course_type: 'ce',
    color: '#1E3A5F'
  });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  // Admin Management State
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [adminList, setAdminList] = useState([]);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({
    email: '',
    name: '',
    password: ''
  });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  
  // Share Course State
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedCourseForShare, setSelectedCourseForShare] = useState(null);
  const [selectedScheduledCourseForShare, setSelectedScheduledCourseForShare] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Get current admin info
    const adminInfo = getCurrentAdmin();
    setCurrentAdmin(adminInfo);
    fetchData();
    if (adminInfo?.is_primary) {
      fetchAdmins();
    }
  }, []);

  useEffect(() => {
    fetchMonthSchedule();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [coursesRes, statsRes, instructorsRes, registrationsRes, requestsRes, sponsorsRes] = await Promise.all([
        axios.get(`${API_URL}/courses`, { headers }),
        axios.get(`${API_URL}/dashboard/stats`, { headers }),
        axios.get(`${API_URL}/instructors`, { headers }),
        axios.get(`${API_URL}/registrations`, { headers }),
        axios.get(`${API_URL}/course-requests`, { headers }),
        axios.get(`${API_URL}/sponsors`, { headers })
      ]);
      setCourses(coursesRes.data);
      setStats(statsRes.data);
      setInstructors(instructorsRes.data);
      setRegistrations(registrationsRes.data);
      setCourseRequests(requestsRes.data);
      setSponsors(sponsorsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/admin/list`, { headers });
      setAdminList(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdminForm.email || !newAdminForm.name || !newAdminForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newAdminForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreatingAdmin(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/admin/create`, newAdminForm, { headers });
      toast.success('Admin created successfully');
      setShowAdminDialog(false);
      setNewAdminForm({ email: '', name: '', password: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${adminEmail} as an admin?`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/admin/${adminId}`, { headers });
      toast.success('Admin removed successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const fetchMonthSchedule = async () => {
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const response = await axios.get(`${API_URL}/schedule/month/${year}/${month}`);
      setScheduledCourses(response.data);
    } catch (error) {
      console.error('Failed to fetch schedule');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`${API_URL}/courses/${courseId}`);
      setCourses(courses.filter(c => c.id !== courseId));
      toast.success('Course deleted');
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleToggleCourseType = async (courseId, currentType) => {
    const newType = currentType === 'professional_development' ? 'ce' : 'professional_development';
    const newTypeLabel = newType === 'ce' ? 'CE (Continuing Education)' : 'Training';
    
    if (!window.confirm(`Change this course to ${newTypeLabel}?`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/courses/${courseId}`, { course_type: newType }, { headers });
      toast.success(`Course changed to ${newTypeLabel}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update course type');
    }
  };

  const handleScheduleCourse = async () => {
    if (!scheduleForm.course_id || !scheduleForm.scheduled_date) {
      toast.error('Please select a course and date');
      return;
    }

    try {
      await axios.post(`${API_URL}/schedule`, scheduleForm);
      toast.success('Course scheduled successfully');
      setShowScheduleDialog(false);
      setScheduleForm({
        course_id: '',
        scheduled_date: '',
        scheduled_time: '09:00',
        delivery_format: 'webinar',
        location: '',
        notes: '',
        instructor_id: ''
      });
      fetchMonthSchedule();
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule course');
    }
  };

  const handleDeleteScheduled = async (scheduleId) => {
    if (!window.confirm('Remove this scheduled course?')) return;
    
    try {
      await axios.delete(`${API_URL}/schedule/${scheduleId}`);
      toast.success('Schedule removed');
      fetchMonthSchedule();
      fetchData();
    } catch (error) {
      toast.error('Failed to remove schedule');
    }
  };

  const handleSaveInstructor = async () => {
    if (!instructorForm.name.trim()) {
      toast.error('Please enter instructor name');
      return;
    }

    try {
      const data = {
        ...instructorForm,
        specialties: instructorForm.specialties ? instructorForm.specialties.split(',').map(s => s.trim()).filter(s => s) : []
      };

      if (editingInstructor) {
        await axios.put(`${API_URL}/instructors/${editingInstructor.id}`, data);
        toast.success('Instructor updated');
      } else {
        await axios.post(`${API_URL}/instructors`, data);
        toast.success('Instructor added');
      }
      
      setShowInstructorDialog(false);
      setEditingInstructor(null);
      setInstructorForm({ name: '', title: '', bio: '', email: '', phone: '', website: '', specialties: '', photo_url: '', certification_pdf_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save instructor');
    }
  };

  const handleEditInstructor = (instructor) => {
    setEditingInstructor(instructor);
    setInstructorForm({
      name: instructor.name || '',
      title: instructor.title || '',
      bio: instructor.bio || '',
      email: instructor.email || '',
      phone: instructor.phone || '',
      website: instructor.website || '',
      specialties: (instructor.specialties || []).join(', '),
      photo_url: instructor.photo_url || '',
      certification_pdf_url: instructor.certification_pdf_url || ''
    });
    setShowInstructorDialog(true);
  };

  const handleDeleteInstructor = async (instructorId) => {
    if (!window.confirm('Delete this instructor?')) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/instructors/${instructorId}`, { headers });
      toast.success('Instructor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete instructor');
    }
  };

  const handleInviteInstructor = async (instructorId, instructorName, instructorEmail) => {
    if (!instructorEmail) {
      toast.error('Instructor must have an email to receive an invitation');
      return;
    }
    
    if (!window.confirm(`Send login invitation to ${instructorName} (${instructorEmail})?`)) return;
    
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/instructor/invite`, { instructor_id: instructorId }, { headers });
      toast.success(`Invitation sent to ${instructorEmail}`);
      
      // Log the invite link for development
      if (response.data.invite_link) {
        console.log('Invite link:', response.data.invite_link);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleInviteNewInstructor = async () => {
    if (!inviteInstructorForm.name.trim()) {
      toast.error('Please enter the instructor name');
      return;
    }
    if (!inviteInstructorForm.email.trim()) {
      toast.error('Please enter the instructor email');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteInstructorForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingInvite(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/instructor/invite-new`, inviteInstructorForm, { headers });
      
      toast.success(`Invitation sent to ${inviteInstructorForm.email}!`);
      
      // Log the invite link for development
      if (response.data.invite_link) {
        console.log('Invite link:', response.data.invite_link);
      }
      
      setShowInviteInstructorDialog(false);
      setInviteInstructorForm({ name: '', email: '', bio: '', phone: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  // Sponsor Invite and Approval Handlers
  const handleInviteSponsor = async () => {
    if (!inviteSponsorForm.company_name.trim()) {
      toast.error('Please enter the company name');
      return;
    }
    if (!inviteSponsorForm.contact_name.trim()) {
      toast.error('Please enter the contact name');
      return;
    }
    if (!inviteSponsorForm.email.trim()) {
      toast.error('Please enter the email');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteSponsorForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setSendingSponsorInvite(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(`${API_URL}/sponsor/invite`, inviteSponsorForm, { headers });
      
      toast.success(`Invitation sent to ${inviteSponsorForm.email}!`);
      
      if (response.data.signup_link) {
        console.log('Sponsor signup link:', response.data.signup_link);
      }
      
      setShowInviteSponsorDialog(false);
      setInviteSponsorForm({ company_name: '', contact_name: '', email: '', phone: '', message: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingSponsorInvite(false);
    }
  };

  const handleApproveSponsor = async (sponsorId, companyName) => {
    if (!window.confirm(`Approve ${companyName} as a sponsor? They will be able to log in and view available courses.`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/sponsor/${sponsorId}/approve`, {}, { headers });
      toast.success(`${companyName} has been approved!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve sponsor');
    }
  };

  const handleRejectSponsor = async (sponsorId, companyName) => {
    if (!window.confirm(`Reject ${companyName}'s sponsor application?`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/sponsor/${sponsorId}/reject`, {}, { headers });
      toast.success(`${companyName}'s application has been rejected`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject sponsor');
    }
  };

  const handleGrantFullAccess = async (instructorId, instructorName) => {
    if (!window.confirm(`Grant full admin access to ${instructorName}? They will be able to manage courses, sponsors, and other instructors.`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/instructor/${instructorId}/grant-full-access`, {}, { headers });
      toast.success(`Full access granted to ${instructorName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant full access');
    }
  };

  const handleRevokeFullAccess = async (instructorId, instructorName) => {
    if (!window.confirm(`Revoke full admin access from ${instructorName}?`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/instructor/${instructorId}/revoke-full-access`, {}, { headers });
      toast.success(`Full access revoked from ${instructorName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke full access');
    }
  };

  const handleApproveInstructor = async (instructorId, instructorName) => {
    if (!window.confirm(`Approve ${instructorName} as an instructor? They will be able to log in and will appear on the public website.`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/instructor/${instructorId}/approve`, {}, { headers });
      toast.success(`${instructorName} has been approved!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve instructor');
    }
  };

  const handleRejectInstructor = async (instructorId, instructorName) => {
    if (!window.confirm(`Reject ${instructorName}'s instructor application? They will not be able to log in.`)) return;
    
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/instructor/${instructorId}/reject`, {}, { headers });
      toast.success(`${instructorName}'s application has been rejected`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject instructor');
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/upload/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setInstructorForm({ ...instructorForm, photo_url: response.data.photo_url });
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = () => {
    setInstructorForm({ ...instructorForm, photo_url: '' });
  };

  const handleCertificationUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Invalid file type. Please upload a PDF file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploadingCertification(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/upload/certification`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setInstructorForm({ ...instructorForm, certification_pdf_url: response.data.certification_url });
      toast.success('Certification PDF uploaded');
    } catch (error) {
      toast.error('Failed to upload certification');
    } finally {
      setUploadingCertification(false);
    }
  };

  const removeCertification = () => {
    setInstructorForm({ ...instructorForm, certification_pdf_url: '' });
  };

  // Sponsor Management Functions
  const handleUpdateSponsorStatus = async (sponsorId, status) => {
    try {
      await axios.put(`${API_URL}/sponsors/${sponsorId}`, { status });
      toast.success(`Sponsor ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update sponsor status');
    }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm('Are you sure you want to delete this sponsor?')) return;
    try {
      await axios.delete(`${API_URL}/sponsors/${sponsorId}`);
      toast.success('Sponsor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sponsor');
    }
  };

  const handleLogoUpload = async (sponsorId, file) => {
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/sponsors/${sponsorId}/logo`, formData);
      toast.success('Logo uploaded');
      fetchData();
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const openSponsorAssignDialog = async (scheduled) => {
    setSelectedScheduledCourse(scheduled);
    try {
      const response = await axios.get(`${API_URL}/scheduled-courses/${scheduled.id}/sponsors`);
      setSponsorAssignments(response.data);
    } catch (error) {
      setSponsorAssignments([]);
    }
    setShowSponsorAssignDialog(true);
  };

  const handleAssignSponsor = async (sponsorId, slotPosition) => {
    if (!selectedScheduledCourse) return;
    try {
      await axios.post(`${API_URL}/scheduled-courses/${selectedScheduledCourse.id}/sponsors`, {
        sponsor_id: sponsorId,
        scheduled_course_id: selectedScheduledCourse.id,
        slot_position: slotPosition
      });
      toast.success('Sponsor assigned');
      const response = await axios.get(`${API_URL}/scheduled-courses/${selectedScheduledCourse.id}/sponsors`);
      setSponsorAssignments(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign sponsor');
    }
  };

  const handleRemoveSponsorAssignment = async (sponsorId) => {
    if (!selectedScheduledCourse) return;
    try {
      await axios.delete(`${API_URL}/scheduled-courses/${selectedScheduledCourse.id}/sponsors/${sponsorId}`);
      toast.success('Sponsor removed');
      const response = await axios.get(`${API_URL}/scheduled-courses/${selectedScheduledCourse.id}/sponsors`);
      setSponsorAssignments(response.data);
    } catch (error) {
      toast.error('Failed to remove sponsor');
    }
  };

  // Course Import Handlers
  const handleImportFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/pdf'
      ];
      const ext = file.name.split('.').pop()?.toLowerCase();
      
      if (!validTypes.includes(file.type) && !['docx', 'pdf'].includes(ext)) {
        toast.error('Please upload a DOCX or PDF file');
        return;
      }
      
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 20MB');
        return;
      }
      
      setImportFile(file);
      
      // Auto-fill title from filename if empty
      if (!importForm.title) {
        const nameWithoutExt = file.name.replace(/\.(docx|pdf)$/i, '');
        setImportForm({ ...importForm, title: nameWithoutExt });
      }
    }
  };

  const handleImportCourse = async () => {
    if (!importFile) {
      toast.error('Please select a file to import');
      return;
    }
    
    if (!importForm.title.trim()) {
      toast.error('Please enter a course title');
      return;
    }
    
    if (importForm.credit_hours < 1 || importForm.credit_hours > 8) {
      toast.error('Credit hours must be between 1 and 8');
      return;
    }

    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('title', importForm.title);
      formData.append('description', importForm.description);
      formData.append('credit_hours', importForm.credit_hours.toString());
      formData.append('category', importForm.category);
      formData.append('course_type', importForm.course_type);
      formData.append('color', importForm.color);

      const response = await axios.post(`${API_URL}/courses/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for AI processing
      });

      toast.success(`Course imported! ${response.data.slides_generated} slides generated.`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportForm({
        title: '',
        description: '',
        credit_hours: 1,
        category: 'General',
        course_type: 'ce',
        color: '#1E3A5F'
      });
      fetchData();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import course');
    } finally {
      setImporting(false);
    }
  };

  const getScheduledForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return scheduledCourses.filter(s => s.scheduled_date === dateStr);
  };

  const hasScheduledCourse = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return scheduledCourses.some(s => s.scheduled_date === dateStr);
  };

  const getFormatIcon = (format) => {
    switch (format) {
      case 'webinar': return <Video className="w-4 h-4" />;
      case 'in-person': return <Users className="w-4 h-4" />;
      default: return <Presentation className="w-4 h-4" />;
    }
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 animate-pulse">Loading your courses...</div>
      </div>
    );
  }

  const selectedDateSchedule = getScheduledForDate(selectedDate);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="/images/deallab-logo.png" 
                alt="DealLab" 
                className="h-56 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              {/* Admin User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-slate-600 hover:text-[#1E3A5F]" data-testid="admin-user-menu">
                    <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-sm font-medium">
                      {currentAdmin?.name?.charAt(0) || 'A'}
                    </div>
                    <span className="hidden sm:inline">{currentAdmin?.name || 'Admin'}</span>
                    {currentAdmin?.is_primary && (
                      <Shield className="w-4 h-4 text-amber-500" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm text-slate-500 border-b">
                    <p className="font-medium text-slate-700">{currentAdmin?.name}</p>
                    <p className="text-xs">{currentAdmin?.email}</p>
                    {currentAdmin?.is_primary && (
                      <Badge className="mt-1 bg-amber-100 text-amber-700 text-xs">Primary Admin</Badge>
                    )}
                  </div>
                  {currentAdmin?.is_primary && (
                    <DropdownMenuItem onClick={() => setShowAdminDialog(true)} className="cursor-pointer">
                      <UserCog className="w-4 h-4 mr-2" />
                      Manage Admins
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Schedule Course Dialog */}
              <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-slate-50" data-testid="schedule-course-btn">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Schedule Course
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>Schedule a Course</DialogTitle>
                    <p className="text-sm text-slate-500">Home &gt; My Courses &gt; Add Course</p>
                  </DialogHeader>
                  
                  <div className="space-y-6 mt-4">
                    {/* Course Details Section */}
                    <div className="border-b pb-4">
                      <h3 className="font-semibold text-slate-900 mb-4">Course Details</h3>
                      
                      <div className="space-y-4">
                        {/* Delivery Method */}
                        <div>
                          <Label>Delivery Method:</Label>
                          <Select
                            value={scheduleForm.delivery_format}
                            onValueChange={(v) => setScheduleForm({ ...scheduleForm, delivery_format: v })}
                          >
                            <SelectTrigger data-testid="schedule-format-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="classroom">Classroom</SelectItem>
                              <SelectItem value="webinar">Webinar</SelectItem>
                              <SelectItem value="hybrid">Hybrid</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Instructor */}
                        <div>
                          <Label>Instructor:</Label>
                          <Select
                            value={scheduleForm.instructor_id || "none"}
                            onValueChange={(v) => setScheduleForm({ ...scheduleForm, instructor_id: v === "none" ? "" : v })}
                          >
                            <SelectTrigger data-testid="schedule-instructor-select">
                              <SelectValue placeholder="Select Instructor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Instructor</SelectItem>
                              {instructors.filter(i => i.is_active).map(i => (
                                <SelectItem key={i.id} value={i.id}>
                                  {i.name} {i.title && `- ${i.title}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Course */}
                        <div>
                          <Label>Course:</Label>
                          <Select
                            value={scheduleForm.course_id}
                            onValueChange={(v) => setScheduleForm({ ...scheduleForm, course_id: v })}
                          >
                            <SelectTrigger data-testid="schedule-course-select">
                              <SelectValue placeholder="Please Select a Course" />
                            </SelectTrigger>
                            <SelectContent>
                              {courses.map(c => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.title} ({c.credit_hours}hr {c.course_type === 'professional_development' ? 'Training' : 'CE'})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Time Zone */}
                        <div>
                          <Label>Time Zone:</Label>
                          <Select
                            value={scheduleForm.timezone}
                            onValueChange={(v) => setScheduleForm({ ...scheduleForm, timezone: v })}
                          >
                            <SelectTrigger data-testid="schedule-timezone-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="America/Chicago">(GMT-6:00) Central Time (US and Canada)</SelectItem>
                              <SelectItem value="America/New_York">(GMT-5:00) Eastern Time (US and Canada)</SelectItem>
                              <SelectItem value="America/Denver">(GMT-7:00) Mountain Time (US and Canada)</SelectItem>
                              <SelectItem value="America/Los_Angeles">(GMT-8:00) Pacific Time (US and Canada)</SelectItem>
                              <SelectItem value="America/Phoenix">(GMT-7:00) Arizona</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Date and Time */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Select Date:</Label>
                            <Input
                              type="date"
                              value={scheduleForm.scheduled_date}
                              onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value })}
                              data-testid="schedule-date-input"
                            />
                          </div>
                          <div>
                            <Label>Select Time:</Label>
                            <Select
                              value={scheduleForm.scheduled_time}
                              onValueChange={(v) => setScheduleForm({ ...scheduleForm, scheduled_time: v })}
                            >
                              <SelectTrigger data-testid="schedule-time-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="08:00">08:00 AM</SelectItem>
                                <SelectItem value="09:00">09:00 AM</SelectItem>
                                <SelectItem value="10:00">10:00 AM</SelectItem>
                                <SelectItem value="11:00">11:00 AM</SelectItem>
                                <SelectItem value="12:00">12:00 PM</SelectItem>
                                <SelectItem value="13:00">01:00 PM</SelectItem>
                                <SelectItem value="14:00">02:00 PM</SelectItem>
                                <SelectItem value="15:00">03:00 PM</SelectItem>
                                <SelectItem value="16:00">04:00 PM</SelectItem>
                                <SelectItem value="17:00">05:00 PM</SelectItem>
                                <SelectItem value="18:00">06:00 PM</SelectItem>
                                <SelectItem value="19:00">07:00 PM</SelectItem>
                                <SelectItem value="20:00">08:00 PM</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Venue Details Section - Only show for classroom/in-person */}
                    {(scheduleForm.delivery_format === 'classroom' || scheduleForm.delivery_format === 'hybrid') && (
                      <div className="border-b pb-4">
                        <h3 className="font-semibold text-slate-900 mb-4">Venue Details</h3>
                        
                        <div className="space-y-4">
                          <div>
                            <Label>Venue Name:</Label>
                            <Input
                              placeholder="Enter venue name"
                              value={scheduleForm.venue_name}
                              onChange={(e) => setScheduleForm({ ...scheduleForm, venue_name: e.target.value })}
                              data-testid="schedule-venue-name"
                            />
                          </div>

                          <div>
                            <Label>Address 1:</Label>
                            <Input
                              placeholder="Street address"
                              value={scheduleForm.address_1}
                              onChange={(e) => setScheduleForm({ ...scheduleForm, address_1: e.target.value })}
                              data-testid="schedule-address1"
                            />
                          </div>

                          <div>
                            <Label>Address 2:</Label>
                            <Input
                              placeholder="Suite, unit, building (optional)"
                              value={scheduleForm.address_2}
                              onChange={(e) => setScheduleForm({ ...scheduleForm, address_2: e.target.value })}
                              data-testid="schedule-address2"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <Label>City:</Label>
                              <Input
                                placeholder="City"
                                value={scheduleForm.city}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, city: e.target.value })}
                                data-testid="schedule-city"
                              />
                            </div>
                            <div>
                              <Label>State:</Label>
                              <Select
                                value={scheduleForm.state}
                                onValueChange={(v) => setScheduleForm({ ...scheduleForm, state: v })}
                              >
                                <SelectTrigger data-testid="schedule-state">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="TX">Texas</SelectItem>
                                  <SelectItem value="AL">Alabama</SelectItem>
                                  <SelectItem value="AK">Alaska</SelectItem>
                                  <SelectItem value="AZ">Arizona</SelectItem>
                                  <SelectItem value="AR">Arkansas</SelectItem>
                                  <SelectItem value="CA">California</SelectItem>
                                  <SelectItem value="CO">Colorado</SelectItem>
                                  <SelectItem value="FL">Florida</SelectItem>
                                  <SelectItem value="GA">Georgia</SelectItem>
                                  <SelectItem value="LA">Louisiana</SelectItem>
                                  <SelectItem value="NM">New Mexico</SelectItem>
                                  <SelectItem value="NY">New York</SelectItem>
                                  <SelectItem value="OK">Oklahoma</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label>Zip:</Label>
                              <Input
                                placeholder="Zip code"
                                value={scheduleForm.zip}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, zip: e.target.value })}
                                data-testid="schedule-zip"
                              />
                            </div>
                          </div>

                          <div>
                            <Label>Venue Website:</Label>
                            <Input
                              placeholder="https://"
                              value={scheduleForm.venue_website}
                              onChange={(e) => setScheduleForm({ ...scheduleForm, venue_website: e.target.value })}
                              data-testid="schedule-venue-website"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Webinar Link - Only show for webinar/hybrid */}
                    {(scheduleForm.delivery_format === 'webinar' || scheduleForm.delivery_format === 'hybrid') && (
                      <div className="border-b pb-4">
                        <h3 className="font-semibold text-slate-900 mb-4">Webinar Details</h3>
                        <div>
                          <Label>Meeting Link:</Label>
                          <Input
                            placeholder="e.g., Zoom link"
                            value={scheduleForm.location}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, location: e.target.value })}
                            data-testid="schedule-location-input"
                          />
                        </div>
                      </div>
                    )}

                    {/* Additional Settings */}
                    <div className="border-b pb-4">
                      <h3 className="font-semibold text-slate-900 mb-4">Additional Settings</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <Label>Attendee Limit:</Label>
                          <Input
                            type="number"
                            placeholder="Leave blank for unlimited"
                            value={scheduleForm.attendee_limit}
                            onChange={(e) => setScheduleForm({ ...scheduleForm, attendee_limit: e.target.value })}
                            data-testid="schedule-attendee-limit"
                          />
                        </div>

                        <div className="space-y-3">
                          <div>
                            <Label>Privacy:</Label>
                            <div className="flex items-center space-x-2 mt-2">
                              <input
                                type="checkbox"
                                id="is_private"
                                checked={scheduleForm.is_private}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, is_private: e.target.checked })}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                              <label htmlFor="is_private" className="text-sm text-slate-600">
                                Mark this course as private. It will not display on your public course schedule.
                              </label>
                            </div>
                          </div>

                          <div>
                            <Label>Test Event:</Label>
                            <div className="flex items-center space-x-2 mt-2">
                              <input
                                type="checkbox"
                                id="is_test_event"
                                checked={scheduleForm.is_test_event}
                                onChange={(e) => setScheduleForm({ ...scheduleForm, is_test_event: e.target.checked })}
                                className="w-4 h-4 rounded border-slate-300"
                              />
                              <label htmlFor="is_test_event" className="text-sm text-slate-600">
                                Enabled
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Agreement */}
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <Label>Agreement:</Label>
                      <p className="text-sm text-slate-600 mt-2 mb-3">
                        By submitting this course, you agree that any attendees that have not signed up for the course online 
                        or have not been added manually via the admin portal within a period of 48 hours after course completion 
                        will not receive continuing education credits. You will not be able to add additional attendees after 
                        this period has passed.
                      </p>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="agreement_accepted"
                          checked={scheduleForm.agreement_accepted}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, agreement_accepted: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <label htmlFor="agreement_accepted" className="text-sm font-medium text-slate-700">
                          I agree to the terms above
                        </label>
                      </div>
                    </div>

                    <Button 
                      onClick={handleScheduleCourse} 
                      className="w-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white"
                      disabled={!scheduleForm.agreement_accepted}
                      data-testid="confirm-schedule-btn"
                    >
                      <CalendarDays className="w-4 h-4 mr-2" />
                      Schedule Course
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Add Instructor Dialog */}
              <Dialog open={showInstructorDialog} onOpenChange={(open) => {
                setShowInstructorDialog(open);
                if (!open) {
                  setEditingInstructor(null);
                  setInstructorForm({ name: '', title: '', bio: '', email: '', phone: '', specialties: '', photo_url: '', certification_pdf_url: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-sky-500 text-sky-600 hover:bg-sky-50" data-testid="add-instructor-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Instructor
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                      {editingInstructor ? 'Edit Instructor' : 'Add New Instructor'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* Photo Upload Section */}
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {instructorForm.photo_url ? (
                          <div className="relative">
                            <img
                              src={instructorForm.photo_url.startsWith('/api') 
                                ? `${process.env.REACT_APP_BACKEND_URL}${instructorForm.photo_url}` 
                                : instructorForm.photo_url}
                              alt="Instructor"
                              className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                            />
                            <button
                              onClick={removePhoto}
                              className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                              type="button"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1E3A5F] to-sky-500 flex items-center justify-center text-white text-2xl font-bold">
                            {instructorForm.name ? instructorForm.name.charAt(0).toUpperCase() : <Camera className="w-8 h-8 opacity-70" />}
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <Label className="mb-2 block">Profile Photo</Label>
                        <div className="flex items-center gap-2">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/gif"
                              onChange={handlePhotoUpload}
                              className="hidden"
                              data-testid="instructor-photo-input"
                            />
                            <div className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50 transition-colors ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
                              {uploadingPhoto ? (
                                <>
                                  <div className="w-4 h-4 mr-2 border-2 border-slate-300 border-t-sky-500 rounded-full animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload Photo
                                </>
                              )}
                            </div>
                          </label>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">JPEG, PNG, WebP or GIF. Max 5MB</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          placeholder="Full name"
                          value={instructorForm.name}
                          onChange={(e) => setInstructorForm({ ...instructorForm, name: e.target.value })}
                          data-testid="instructor-name-input"
                        />
                      </div>
                      <div>
                        <Label>Title</Label>
                        <Input
                          placeholder="e.g., Senior Loan Officer"
                          value={instructorForm.title}
                          onChange={(e) => setInstructorForm({ ...instructorForm, title: e.target.value })}
                          data-testid="instructor-title-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Bio</Label>
                      <Textarea
                        placeholder="Brief biography and credentials..."
                        value={instructorForm.bio}
                        onChange={(e) => setInstructorForm({ ...instructorForm, bio: e.target.value })}
                        rows={4}
                        data-testid="instructor-bio-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          placeholder="email@example.com"
                          value={instructorForm.email}
                          onChange={(e) => setInstructorForm({ ...instructorForm, email: e.target.value })}
                          data-testid="instructor-email-input"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          placeholder="(555) 123-4567"
                          value={instructorForm.phone}
                          onChange={(e) => setInstructorForm({ ...instructorForm, phone: e.target.value })}
                          data-testid="instructor-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input
                        type="url"
                        placeholder="https://yourwebsite.com"
                        value={instructorForm.website || ''}
                        onChange={(e) => setInstructorForm({ ...instructorForm, website: e.target.value })}
                        data-testid="instructor-website-input"
                      />
                    </div>
                    <div>
                      <Label>Specialties</Label>
                      <Input
                        placeholder="e.g., DSCR Loans, Investment Properties (comma separated)"
                        value={instructorForm.specialties}
                        onChange={(e) => setInstructorForm({ ...instructorForm, specialties: e.target.value })}
                        data-testid="instructor-specialties-input"
                      />
                    </div>
                    
                    {/* PSI Certification PDF Upload */}
                    <div className="border-t pt-4 mt-4">
                      <Label className="flex items-center gap-2 mb-2">
                        <FileCheck className="w-4 h-4 text-emerald-600" />
                        PSI Certification Letter (PDF)
                      </Label>
                      {instructorForm.certification_pdf_url ? (
                        <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <div className="flex items-center gap-2">
                            <FileCheck className="w-5 h-5 text-emerald-600" />
                            <span className="text-sm text-emerald-700">Certification uploaded</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${instructorForm.certification_pdf_url}`, '_blank')}
                            >
                              View PDF
                            </Button>
                            <button
                              onClick={removeCertification}
                              className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                              type="button"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <label className="cursor-pointer flex-1">
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={handleCertificationUpload}
                              className="hidden"
                              data-testid="instructor-certification-input"
                            />
                            <div className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-700 transition-colors ${uploadingCertification ? 'opacity-50 pointer-events-none' : ''}`}>
                              {uploadingCertification ? (
                                <>
                                  <div className="w-4 h-4 mr-2 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload Certification PDF
                                </>
                              )}
                            </div>
                          </label>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-1">Upload the Preferred Systems instructor approval letter. Max 10MB</p>
                    </div>

                    <Button 
                      onClick={handleSaveInstructor} 
                      className="w-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white"
                      data-testid="save-instructor-btn"
                    >
                      <User className="w-4 h-4 mr-2" />
                      {editingInstructor ? 'Update Instructor' : 'Add Instructor'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Import Course Dialog */}
              <Dialog open={showImportDialog} onOpenChange={(open) => {
                setShowImportDialog(open);
                if (!open) {
                  setImportFile(null);
                  setImportForm({
                    title: '',
                    description: '',
                    credit_hours: 1,
                    category: 'General',
                    course_type: 'ce',
                    color: '#1E3A5F'
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50" data-testid="import-course-btn">
                    <FileText className="w-4 h-4 mr-2" />
                    Import Course
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                      Import Course from Document
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* File Upload */}
                    <div>
                      <Label>Course Document (DOCX or PDF)</Label>
                      <div className="mt-2">
                        {importFile ? (
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-2">
                              <FileText className="w-5 h-5 text-slate-500" />
                              <span className="text-sm text-slate-700 truncate max-w-[200px]">{importFile.name}</span>
                            </div>
                            <button
                              onClick={() => setImportFile(null)}
                              className="p-1 hover:bg-slate-200 rounded"
                              type="button"
                            >
                              <X className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              onChange={handleImportFileChange}
                              className="hidden"
                              data-testid="import-file-input"
                            />
                            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors">
                              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                              <p className="text-sm text-slate-600">Click to upload DOCX or PDF</p>
                              <p className="text-xs text-slate-400 mt-1">Max 20MB</p>
                            </div>
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Course Title */}
                    <div>
                      <Label>Course Title</Label>
                      <Input
                        value={importForm.title}
                        onChange={(e) => setImportForm({ ...importForm, title: e.target.value })}
                        placeholder="Enter course title"
                        data-testid="import-title-input"
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={importForm.description}
                        onChange={(e) => setImportForm({ ...importForm, description: e.target.value })}
                        placeholder="Brief course description"
                        rows={2}
                        data-testid="import-description-input"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Course Type */}
                      <div>
                        <Label>Course Type</Label>
                        <Select
                          value={importForm.course_type}
                          onValueChange={(v) => setImportForm({ ...importForm, course_type: v })}
                        >
                          <SelectTrigger data-testid="import-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ce">CE Course</SelectItem>
                            <SelectItem value="professional_development">Professional Development</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Hours */}
                      <div>
                        <Label>{importForm.course_type === 'professional_development' ? 'Duration (Hours)' : 'CE Hours'}</Label>
                        <Select
                          value={importForm.credit_hours.toString()}
                          onValueChange={(v) => setImportForm({ ...importForm, credit_hours: parseInt(v) })}
                        >
                          <SelectTrigger data-testid="import-hours-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(h => (
                              <SelectItem key={h} value={h.toString()}>
                                {h} hour{h > 1 ? 's' : ''} ({h * 30} slides)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Category */}
                      <div>
                        <Label>Category</Label>
                        <Select
                          value={importForm.category}
                          onValueChange={(v) => setImportForm({ ...importForm, category: v })}
                        >
                          <SelectTrigger data-testid="import-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="General">General</SelectItem>
                            <SelectItem value="Mortgage & Lending">Mortgage & Lending</SelectItem>
                            <SelectItem value="Investment Properties">Investment Properties</SelectItem>
                            <SelectItem value="Professional Development">Professional Development</SelectItem>
                            <SelectItem value="Legal & Compliance">Legal & Compliance</SelectItem>
                            <SelectItem value="Technology">Technology</SelectItem>
                            <SelectItem value="Marketing">Marketing</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Theme Color */}
                      <div>
                        <Label>Theme Color</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <input
                            type="color"
                            value={importForm.color}
                            onChange={(e) => setImportForm({ ...importForm, color: e.target.value })}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <span className="text-sm text-slate-500">{importForm.color}</span>
                        </div>
                      </div>
                    </div>

                    {/* Info box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <strong>AI-Powered Slide Generation:</strong> The document content will be analyzed and converted into {importForm.credit_hours * 30} professional presentation slides automatically.
                      </p>
                    </div>

                    {/* Import Button */}
                    <Button
                      onClick={handleImportCourse}
                      disabled={importing || !importFile || !importForm.title.trim()}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="confirm-import-btn"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Slides...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Import & Generate Slides
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Link to="/new">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="add-course-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Course
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-slate-200" data-testid="stat-courses">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1E3A5F]/10 text-[#1E3A5F] flex items-center justify-center">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_courses || 0}</p>
                  <p className="text-xs text-slate-500">Courses</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-slides">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-600 flex items-center justify-center">
                  <Monitor className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_slides || 0}</p>
                  <p className="text-xs text-slate-500">Slides</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-hours">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_ce_hours || 0}</p>
                  <p className="text-xs text-slate-500">CE Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-scheduled">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.upcoming_scheduled?.length || 0}</p>
                  <p className="text-xs text-slate-500">Upcoming</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stat-instructors">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{instructors.length}</p>
                  <p className="text-xs text-slate-500">Instructors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="schedule" className="mb-8">
          <TabsList className="mb-6">
            <TabsTrigger value="schedule" className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="registrations" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Registrations
              {registrations.length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-sky-100 text-sky-700">{registrations.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="sponsors" className="flex items-center gap-2">
              <Handshake className="w-4 h-4" />
              Sponsors
              {sponsors.filter(s => s.status === 'pending').length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-amber-100 text-amber-700">
                  {sponsors.filter(s => s.status === 'pending').length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="instructors" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Instructors
            </TabsTrigger>
            <TabsTrigger value="courses" className="flex items-center gap-2">
              <Layers className="w-4 h-4" />
              Courses
            </TabsTrigger>
          </TabsList>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Calendar */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <CalendarDays className="w-5 h-5 text-[#1E3A5F]" />
                    Course Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-md border w-full"
                    modifiers={{
                      scheduled: (date) => hasScheduledCourse(date)
                    }}
                    modifiersStyles={{
                      scheduled: {
                        backgroundColor: '#0EA5E9',
                        color: 'white',
                        borderRadius: '50%'
                      }
                    }}
                  />
                </CardContent>
              </Card>

              {/* Selected Date Details */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle 
                    className="text-lg"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {selectedDate.toLocaleDateString('en-US', { 
                      weekday: 'long',
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedDateSchedule.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">No courses scheduled</p>
                      <Button 
                        variant="link" 
                        className="text-sky-600 mt-2"
                        onClick={() => {
                          setScheduleForm({
                            ...scheduleForm,
                            scheduled_date: selectedDate.toISOString().split('T')[0]
                          });
                          setShowScheduleDialog(true);
                        }}
                      >
                        Schedule a course
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedDateSchedule.map((scheduled) => (
                        <div 
                          key={scheduled.id}
                          className="p-3 rounded-lg border border-slate-200 hover:border-sky-300 transition-colors"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div 
                              className="w-2 h-2 rounded-full mt-2"
                              style={{ backgroundColor: scheduled.color }}
                            />
                            <Badge variant="outline" className="text-xs">
                              {getFormatIcon(scheduled.delivery_format)}
                              <span className="ml-1 capitalize">{scheduled.delivery_format}</span>
                            </Badge>
                          </div>
                          <h4 className="font-medium text-slate-900 mb-1">{scheduled.course_title}</h4>
                          
                          {/* Instructor info */}
                          {scheduled.instructor_name && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                              <User className="w-3 h-3" />
                              <span>{scheduled.instructor_name}</span>
                              {scheduled.instructor_title && (
                                <span className="text-slate-400">• {scheduled.instructor_title}</span>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                            <Clock className="w-3 h-3" />
                            {formatTime(scheduled.scheduled_time)}
                            <span className="text-slate-300">•</span>
                            {scheduled.credit_hours}hr CE
                          </div>
                          {scheduled.location && (
                            <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate">{scheduled.location}</span>
                            </div>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Link to={`/present/${scheduled.course_id}/${scheduled.id}`} className="flex-1">
                              <Button size="sm" className="w-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white">
                                <Play className="w-3 h-3 mr-1" />
                                Present
                              </Button>
                            </Link>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const course = courses.find(c => c.id === scheduled.course_id);
                                if (course) {
                                  setSelectedCourseForShare(course);
                                  setSelectedScheduledCourseForShare(scheduled);
                                  setShowShareDialog(true);
                                }
                              }}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-300"
                              title="Share & Market"
                              data-testid={`share-scheduled-${scheduled.id}`}
                            >
                              <Share2 className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openSponsorAssignDialog(scheduled);
                              }}
                              className="text-sky-600 hover:text-sky-700 hover:bg-sky-50 border-sky-300"
                              title="Manage Sponsors"
                              data-testid={`manage-sponsors-${scheduled.id}`}
                            >
                              <Handshake className="w-3 h-3 mr-1" />
                              Sponsors
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeleteScheduled(scheduled.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Upcoming Schedule List */}
            {stats?.upcoming_scheduled?.length > 0 && (
              <Card className="border-slate-200 mt-8">
                <CardHeader>
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <Clock className="w-5 h-5 text-sky-500" />
                    Upcoming Courses
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stats.upcoming_scheduled.slice(0, 6).map((scheduled) => (
                      <div 
                        key={scheduled.id}
                        className="p-4 rounded-lg border border-slate-200 hover:shadow-md transition-all"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: scheduled.color }}
                          />
                          <span className="text-sm font-medium text-slate-900">
                            {new Date(scheduled.scheduled_date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                          <span className="text-sm text-slate-400">
                            {formatTime(scheduled.scheduled_time)}
                          </span>
                        </div>
                        <h4 className="font-medium text-slate-900">{scheduled.course_title}</h4>
                        {scheduled.instructor_name && (
                          <p className="text-sm text-slate-500 mt-1">
                            <User className="w-3 h-3 inline mr-1" />
                            {scheduled.instructor_name}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                          {getFormatIcon(scheduled.delivery_format)}
                          <span className="capitalize">{scheduled.delivery_format}</span>
                          <span className="text-slate-300">•</span>
                          {scheduled.credit_hours}hr CE
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Registrations Tab */}
          <TabsContent value="registrations">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Course Registrations */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Course Registrations
                    <Badge variant="secondary" className="ml-2">{registrations.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {registrations.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No registrations yet</p>
                      <p className="text-sm">Registrations will appear here when agents sign up for courses</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {registrations.map((reg) => (
                        <div 
                          key={reg.id} 
                          className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-[#1E3A5F]">
                                {reg.first_name} {reg.last_name}
                              </p>
                              <p className="text-sm text-slate-600">{reg.course_title}</p>
                            </div>
                            <Badge 
                              variant="outline" 
                              className="bg-emerald-50 text-emerald-700 border-emerald-200"
                            >
                              {reg.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {reg.email}
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {reg.phone}
                            </div>
                            <div className="flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {reg.company || 'N/A'}
                            </div>
                            <div className="flex items-center gap-1">
                              <Award className="w-3 h-3" />
                              TREC: {reg.license_number}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                            <CalendarDays className="w-3 h-3" />
                            {new Date(reg.scheduled_date).toLocaleDateString('en-US', { 
                              weekday: 'short', month: 'short', day: 'numeric' 
                            })}
                            <span className="text-slate-300">•</span>
                            Registered: {new Date(reg.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Course Requests */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <FileText className="w-5 h-5 text-amber-600" />
                    Course Requests
                    <Badge variant="secondary" className="ml-2">{courseRequests.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {courseRequests.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No course requests yet</p>
                      <p className="text-sm">Requests from brokerages will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {courseRequests.map((req) => (
                        <div 
                          key={req.id} 
                          className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-[#1E3A5F]">
                                {req.first_name} {req.last_name}
                              </p>
                              <p className="text-sm text-slate-600">{req.company}</p>
                            </div>
                            <Badge 
                              variant="outline" 
                              className={req.status === 'pending' 
                                ? 'bg-amber-50 text-amber-700 border-amber-200' 
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200'}
                            >
                              {req.status}
                            </Badge>
                          </div>
                          {req.course_title && (
                            <p className="text-sm text-sky-600 mb-2">
                              Requested: {req.course_title}
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {req.email}
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {req.phone}
                            </div>
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {req.estimated_attendees} attendees
                            </div>
                            <div className="flex items-center gap-1">
                              <Monitor className="w-3 h-3" />
                              {req.delivery_preference}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-slate-500">
                            <p className="font-medium mb-1">Preferred Dates:</p>
                            <div className="flex flex-wrap gap-2">
                              {req.preferred_date_1 && (
                                <span className="px-2 py-0.5 bg-white rounded border border-slate-200">
                                  {new Date(req.preferred_date_1).toLocaleDateString()}
                                </span>
                              )}
                              {req.preferred_date_2 && (
                                <span className="px-2 py-0.5 bg-white rounded border border-slate-200">
                                  {new Date(req.preferred_date_2).toLocaleDateString()}
                                </span>
                              )}
                              {req.preferred_date_3 && (
                                <span className="px-2 py-0.5 bg-white rounded border border-slate-200">
                                  {new Date(req.preferred_date_3).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          {req.notes && (
                            <p className="mt-2 text-xs text-slate-400 italic">
                              "{req.notes}"
                            </p>
                          )}
                          <div className="text-xs text-slate-400 mt-2">
                            Submitted: {new Date(req.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Sponsors Tab */}
          <TabsContent value="sponsors">
            {/* Invite Sponsor Button */}
            <div className="flex justify-end mb-6">
              <Button 
                onClick={() => setShowInviteSponsorDialog(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="invite-sponsor-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Invite Sponsor
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Pending Sponsors */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <Clock className="w-5 h-5 text-amber-600" />
                    Pending Applications
                    <Badge variant="secondary" className="ml-2">
                      {sponsors.filter(s => s.status === 'pending' || s.status === 'invited').length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sponsors.filter(s => s.status === 'pending' || s.status === 'invited').length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Handshake className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No pending sponsor applications</p>
                      <p className="text-sm mt-1">Click "Invite Sponsor" to send invitations</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {sponsors.filter(s => s.status === 'pending' || s.status === 'invited').map((sponsor) => (
                        <div 
                          key={sponsor.id} 
                          className="p-4 bg-amber-50 rounded-lg border border-amber-100"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-[#1E3A5F]">{sponsor.company_name}</p>
                              <p className="text-sm text-slate-600">{sponsor.contact_name}</p>
                            </div>
                            <Badge variant="outline" className={`${sponsor.status === 'invited' ? 'bg-slate-100 text-slate-600 border-slate-300' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                              {sponsor.status === 'invited' ? 'Invited' : 'Pending Approval'}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-3">
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {sponsor.email}
                            </div>
                            {sponsor.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {sponsor.phone}
                              </div>
                            )}
                            {sponsor.website && (
                              <div className="flex items-center gap-1 col-span-2">
                                <Globe className="w-3 h-3" />
                                {sponsor.website}
                              </div>
                            )}
                          </div>
                          {sponsor.description && (
                            <p className="text-xs text-slate-600 mb-3 line-clamp-2">{sponsor.description}</p>
                          )}
                          {sponsor.why_sponsor && (
                            <p className="text-xs text-slate-500 italic mb-3">"{sponsor.why_sponsor}"</p>
                          )}
                          {sponsor.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => handleApproveSponsor(sponsor.id, sponsor.company_name)}
                                data-testid={`approve-sponsor-${sponsor.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                onClick={() => handleRejectSponsor(sponsor.id, sponsor.company_name)}
                                data-testid={`reject-sponsor-${sponsor.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          {sponsor.status === 'invited' && (
                            <p className="text-xs text-slate-400 italic">Waiting for sponsor to complete signup</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Approved Sponsors */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    Approved Sponsors
                    <Badge variant="secondary" className="ml-2">
                      {sponsors.filter(s => s.status === 'approved').length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sponsors.filter(s => s.status === 'approved').length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Building className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No approved sponsors yet</p>
                      <p className="text-sm">Approve pending applications to add sponsors</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {sponsors.filter(s => s.status === 'approved').map((sponsor) => (
                        <div 
                          key={sponsor.id} 
                          className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                        >
                          <div className="flex gap-4">
                            {/* Logo */}
                            <div className="shrink-0">
                              {sponsor.logo_url ? (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                                  alt={sponsor.company_name}
                                  className="w-16 h-16 object-contain rounded border border-slate-200 bg-white"
                                />
                              ) : (
                                <div className="w-16 h-16 bg-slate-200 rounded flex items-center justify-center">
                                  <ImageIcon className="w-6 h-6 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-semibold text-[#1E3A5F]">{sponsor.company_name}</p>
                                  <p className="text-sm text-slate-600">{sponsor.contact_name}</p>
                                </div>
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                  Approved
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {sponsor.email}
                                </span>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        handleLogoUpload(sponsor.id, e.target.files[0]);
                                      }
                                    }}
                                  />
                                  <div className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50">
                                    <Upload className="w-3 h-3 mr-1" />
                                    {sponsor.logo_url ? 'Update Logo' : 'Upload Logo'}
                                  </div>
                                </label>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="h-7 text-xs text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteSponsor(sponsor.id)}
                                >
                                  <Trash2 className="w-3 h-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sponsor Assignment Info */}
            <Card className="border-slate-200 mt-8">
              <CardHeader>
                <CardTitle 
                  className="text-xl flex items-center gap-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <Presentation className="w-5 h-5 text-sky-600" />
                  Assign Sponsors to Courses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  To assign sponsors to a scheduled course, go to the <strong>Schedule</strong> tab, 
                  find the course, and click "Manage Sponsors". Each course can have up to 4 sponsor slots.
                </p>
                <Link to="/public/sponsors" target="_blank">
                  <Button variant="outline" className="border-sky-500 text-sky-600">
                    <Globe className="w-4 h-4 mr-2" />
                    View Public Sponsor Page
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Instructors Tab */}
          <TabsContent value="instructors">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle 
                    className="text-xl flex items-center gap-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    <Users className="w-5 h-5 text-[#1E3A5F]" />
                    Instructors
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button 
                      onClick={() => setShowInviteInstructorDialog(true)}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="invite-instructor-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Invite Instructor
                    </Button>
                    <Button 
                      onClick={() => setShowInstructorDialog(true)}
                      className="bg-sky-500 hover:bg-sky-600 text-white"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Instructor
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {instructors.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">No instructors yet</h3>
                    <p className="text-sm mb-4">Add instructors to assign them to scheduled courses</p>
                    <Button 
                      onClick={() => setShowInstructorDialog(true)}
                      className="bg-[#1E3A5F] hover:bg-[#162d4a] text-white"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add First Instructor
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {instructors.map((instructor) => (
                      <Card key={instructor.id} className="border-slate-200">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            {instructor.photo_url ? (
                              <img
                                src={instructor.photo_url.startsWith('/api') 
                                  ? `${process.env.REACT_APP_BACKEND_URL}${instructor.photo_url}` 
                                  : instructor.photo_url}
                                alt={instructor.name}
                                className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1E3A5F] to-sky-500 flex items-center justify-center text-white text-2xl font-bold">
                                {instructor.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditInstructor(instructor)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                {instructor.email && !instructor.can_login && (
                                  <DropdownMenuItem 
                                    onClick={() => handleInviteInstructor(instructor.id, instructor.name, instructor.email)}
                                    className="text-sky-600"
                                  >
                                    <Send className="w-4 h-4 mr-2" />
                                    Send Login Invite
                                  </DropdownMenuItem>
                                )}
                                {instructor.can_login && currentAdmin?.is_primary && !instructor.has_full_access && (
                                  <DropdownMenuItem 
                                    onClick={() => handleGrantFullAccess(instructor.id, instructor.name)}
                                    className="text-emerald-600"
                                  >
                                    <ShieldCheck className="w-4 h-4 mr-2" />
                                    Grant Full Access
                                  </DropdownMenuItem>
                                )}
                                {instructor.has_full_access && currentAdmin?.is_primary && (
                                  <DropdownMenuItem 
                                    onClick={() => handleRevokeFullAccess(instructor.id, instructor.name)}
                                    className="text-amber-600"
                                  >
                                    <ShieldOff className="w-4 h-4 mr-2" />
                                    Revoke Full Access
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteInstructor(instructor.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          
                          {/* Status badges */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {instructor.approval_status === 'pending' && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending Approval
                              </Badge>
                            )}
                            {instructor.approval_status === 'invited' && (
                              <Badge className="bg-slate-100 text-slate-600 text-xs">
                                <Mail className="w-3 h-3 mr-1" />
                                Invited
                              </Badge>
                            )}
                            {instructor.approval_status === 'rejected' && (
                              <Badge className="bg-red-100 text-red-700 text-xs">
                                <XCircle className="w-3 h-3 mr-1" />
                                Rejected
                              </Badge>
                            )}
                            {(instructor.approval_status === 'approved' || (!instructor.approval_status && instructor.is_active)) && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            )}
                            {instructor.can_login && (
                              <Badge className="bg-sky-100 text-sky-700 text-xs">
                                <UserCheck className="w-3 h-3 mr-1" />
                                Can Login
                              </Badge>
                            )}
                            {instructor.has_full_access && (
                              <Badge className="bg-violet-100 text-violet-700 text-xs">
                                <ShieldCheck className="w-3 h-3 mr-1" />
                                Full Access
                              </Badge>
                            )}
                          </div>
                          
                          {/* Approval Actions for Pending Instructors */}
                          {instructor.approval_status === 'pending' && (
                            <div className="flex gap-2 mb-3">
                              <Button
                                size="sm"
                                onClick={() => handleApproveInstructor(instructor.id, instructor.name)}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1"
                                data-testid={`approve-${instructor.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectInstructor(instructor.id, instructor.name)}
                                className="text-red-600 border-red-300 hover:bg-red-50 flex-1"
                                data-testid={`reject-${instructor.id}`}
                              >
                                <XCircle className="w-3 h-3 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                          
                          <h3 className="font-semibold text-slate-900 text-lg">{instructor.name}</h3>
                          {instructor.title && (
                            <p className="text-sky-600 text-sm font-medium">{instructor.title}</p>
                          )}
                          
                          {instructor.bio && (
                            <p className="text-slate-500 text-sm mt-3 line-clamp-3">{instructor.bio}</p>
                          )}
                          
                          {instructor.specialties?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {instructor.specialties.map((spec, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">
                                  {spec}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                            {instructor.email && (
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Mail className="w-4 h-4" />
                                <span className="truncate">{instructor.email}</span>
                              </div>
                            )}
                            {instructor.phone && (
                              <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Phone className="w-4 h-4" />
                                <span>{instructor.phone}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="mb-6 flex items-center justify-between">
              <h2 
                className="text-xl font-semibold text-slate-900 flex items-center gap-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                <Layers className="w-5 h-5 text-[#1E3A5F]" />
                Course Library
              </h2>
              
              {/* Course Type Filter */}
              <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                <button
                  onClick={() => setCourseTypeFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    courseTypeFilter === 'all' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="filter-all"
                >
                  All Courses
                  <span className="ml-1.5 text-xs bg-slate-200 px-1.5 py-0.5 rounded-full">
                    {courses.length}
                  </span>
                </button>
                <button
                  onClick={() => setCourseTypeFilter('ce')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    courseTypeFilter === 'ce' 
                      ? 'bg-white text-emerald-700 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="filter-ce"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    CE Courses
                  </span>
                  <span className="ml-1.5 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                    {courses.filter(c => c.course_type !== 'professional_development').length}
                  </span>
                </button>
                <button
                  onClick={() => setCourseTypeFilter('professional_development')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    courseTypeFilter === 'professional_development' 
                      ? 'bg-white text-amber-700 shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  data-testid="filter-training"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    Training
                  </span>
                  <span className="ml-1.5 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                    {courses.filter(c => c.course_type === 'professional_development').length}
                  </span>
                </button>
              </div>
            </div>

            {courses.length === 0 ? (
              <Card className="border-slate-200 border-dashed">
                <CardContent className="py-12 text-center">
                  <img 
                    src="/images/deallab-logo.png" 
                    alt="DealLab" 
                    className="w-32 mx-auto mb-4 opacity-30"
                  />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No courses yet</h3>
                  <p className="text-slate-500 mb-4">Create your first CE course to get started</p>
                  <Link to="/new">
                    <Button className="bg-[#1E3A5F] hover:bg-[#162d4a] text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Course
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses
                  .filter(course => {
                    if (courseTypeFilter === 'all') return true;
                    if (courseTypeFilter === 'ce') return course.course_type !== 'professional_development';
                    return course.course_type === 'professional_development';
                  })
                  .map((course) => (
                  <Card 
                    key={course.id} 
                    className={`border-slate-200 overflow-hidden transition-all hover:shadow-lg ${!course.is_active ? 'opacity-60' : ''}`}
                    data-testid={`course-card-${course.id}`}
                  >
                    <div 
                      className="h-3"
                      style={{ backgroundColor: course.color }}
                    />
                    
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={course.is_active ? "default" : "secondary"} className="text-xs">
                              {course.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge 
                              variant="outline" 
                              className={`text-xs cursor-pointer transition-all hover:scale-105 ${course.course_type === 'professional_development' ? 'border-amber-500 text-amber-600 bg-amber-50 hover:bg-amber-100' : 'border-emerald-500 text-emerald-600 bg-emerald-50 hover:bg-emerald-100'}`}
                              onClick={() => handleToggleCourseType(course.id, course.course_type)}
                              title={`Click to change to ${course.course_type === 'professional_development' ? 'CE' : 'Training'}`}
                              data-testid={`toggle-type-${course.id}`}
                            >
                              {course.course_type === 'professional_development' ? 'Training' : 'CE'}
                            </Badge>
                          </div>
                          <CardTitle 
                            className="text-lg text-slate-900"
                            style={{ fontFamily: 'Playfair Display, serif' }}
                          >
                            {course.title}
                          </CardTitle>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => {
                                setScheduleForm({ ...scheduleForm, course_id: course.id });
                                setShowScheduleDialog(true);
                              }}
                            >
                              <CalendarDays className="w-4 h-4 mr-2" />
                              Schedule
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedCourseForShare(course);
                                setSelectedScheduledCourseForShare(null);
                                setShowShareDialog(true);
                              }}
                            >
                              <Share2 className="w-4 h-4 mr-2" />
                              Share & Market
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleToggleCourseType(course.id, course.course_type)}
                              className={course.course_type === 'professional_development' ? 'text-emerald-600' : 'text-amber-600'}
                            >
                              <Award className="w-4 h-4 mr-2" />
                              {course.course_type === 'professional_development' ? 'Change to CE' : 'Change to Training'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(course.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">
                        {course.description}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {course.credit_hours} Hr {course.course_type === 'professional_development' ? 'Duration' : 'CE'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Layers className="w-4 h-4" />
                          {course.total_slides} Slides
                        </span>
                      </div>

                      <div className="flex gap-2">
                        <Link to={`/present/${course.id}`} className="flex-1">
                          <Button 
                            className="w-full bg-[#1E3A5F] hover:bg-[#162d4a] text-white"
                            data-testid={`present-${course.id}`}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Present
                          </Button>
                        </Link>
                        <Button 
                          variant="outline" 
                          className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => {
                            setSelectedCourseForShare(course);
                            setSelectedScheduledCourseForShare(null);
                            setShowShareDialog(true);
                          }}
                          data-testid={`share-${course.id}`}
                        >
                          <Share2 className="w-4 h-4" />
                        </Button>
                        <Link to={`/edit/${course.id}`}>
                          <Button variant="outline" className="border-sky-500 text-sky-600 hover:bg-sky-50" data-testid={`edit-${course.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Sponsor Assignment Dialog */}
        <Dialog open={showSponsorAssignDialog} onOpenChange={setShowSponsorAssignDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                Manage Sponsors
              </DialogTitle>
            </DialogHeader>
            {selectedScheduledCourse && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-[#1E3A5F]">{selectedScheduledCourse.course_title}</p>
                  <p className="text-sm text-slate-600">
                    {new Date(selectedScheduledCourse.scheduled_date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })} at {selectedScheduledCourse.scheduled_time}
                  </p>
                </div>

                {/* Current Assignments */}
                <div>
                  <h4 className="font-medium text-sm text-slate-700 mb-2">
                    Assigned Sponsors ({sponsorAssignments.length}/4)
                  </h4>
                  {sponsorAssignments.length === 0 ? (
                    <p className="text-sm text-slate-500 py-2">No sponsors assigned yet</p>
                  ) : (
                    <div className="space-y-2">
                      {sponsorAssignments.map((assignment) => (
                        <div 
                          key={assignment.sponsor_id}
                          className="flex items-center justify-between p-2 bg-emerald-50 rounded border border-emerald-100"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-emerald-200 rounded-full flex items-center justify-center text-xs font-bold text-emerald-700">
                              {assignment.slot_position}
                            </div>
                            {assignment.sponsor?.logo_url ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${assignment.sponsor.logo_url}`}
                                alt=""
                                className="w-8 h-8 object-contain"
                              />
                            ) : null}
                            <span className="text-sm font-medium">{assignment.sponsor?.company_name}</span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveSponsorAssignment(assignment.sponsor_id)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Sponsor */}
                {sponsorAssignments.length < 4 && (
                  <div>
                    <h4 className="font-medium text-sm text-slate-700 mb-2">Add Sponsor</h4>
                    {sponsors.filter(s => s.status === 'approved').length === 0 ? (
                      <p className="text-sm text-slate-500">No approved sponsors available</p>
                    ) : sponsors
                          .filter(s => s.status === 'approved')
                          .filter(s => !sponsorAssignments.find(a => a.sponsor_id === s.id)).length === 0 ? (
                      <p className="text-sm text-slate-500">All approved sponsors are already assigned</p>
                    ) : (
                      <div className="space-y-3">
                        {sponsors
                          .filter(s => s.status === 'approved')
                          .filter(s => !sponsorAssignments.find(a => a.sponsor_id === s.id))
                          .map((sponsor) => (
                            <div 
                              key={sponsor.id}
                              className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                            >
                              <div className="flex items-center gap-3 mb-2">
                                {sponsor.logo_url ? (
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                                    alt=""
                                    className="w-10 h-10 object-contain rounded"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                    <Building className="w-5 h-5 text-slate-400" />
                                  </div>
                                )}
                                <span className="font-medium text-slate-700">{sponsor.company_name}</span>
                              </div>
                              <div className="flex gap-2">
                                {[1, 2, 3, 4]
                                  .filter(slot => !sponsorAssignments.find(a => a.slot_position === slot))
                                  .map(slot => (
                                    <Button
                                      key={slot}
                                      size="sm"
                                      variant="outline"
                                      className="flex-1 text-xs"
                                      onClick={() => handleAssignSponsor(sponsor.id, slot)}
                                    >
                                      Slot {slot}
                                    </Button>
                                  ))
                                }
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-xs text-slate-500 mt-4">
                  <p><strong>Slot positions:</strong></p>
                  <ul className="list-disc list-inside">
                    <li>Slot 1: After course introduction</li>
                    <li>Slot 2: Mid-course break</li>
                    <li>Slot 3: After key content sections</li>
                    <li>Slot 4: Before Q&A/closing</li>
                  </ul>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Admin Management Dialog */}
        <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Shield className="w-5 h-5 text-amber-500" />
                Admin Management
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Add New Admin Form */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-3">Add New Admin</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      placeholder="Enter full name"
                      value={newAdminForm.name}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                      data-testid="new-admin-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      value={newAdminForm.email}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                      data-testid="new-admin-email-input"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      placeholder="Enter password (min 6 characters)"
                      value={newAdminForm.password}
                      onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                      data-testid="new-admin-password-input"
                    />
                  </div>
                  <Button 
                    onClick={handleCreateAdmin}
                    disabled={creatingAdmin}
                    className="w-full bg-[#1E3A5F] hover:bg-[#2a4a6f] text-white"
                    data-testid="create-admin-btn"
                  >
                    {creatingAdmin ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add Admin
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Admin List */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Current Admins ({adminList.length})</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {adminList.map((admin) => (
                    <div 
                      key={admin.id} 
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-medium">
                          {admin.name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-700">{admin.name}</p>
                            {admin.is_primary && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Primary</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{admin.email}</p>
                        </div>
                      </div>
                      {!admin.is_primary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                          data-testid={`delete-admin-${admin.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-slate-500">
                Only the primary admin ({PRIMARY_ADMIN_EMAIL}) can add or remove other admins.
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Course Dialog */}
        <ShareCourseDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          course={selectedCourseForShare}
          scheduledCourse={selectedScheduledCourseForShare}
        />

        {/* Invite New Instructor Dialog */}
        <Dialog open={showInviteInstructorDialog} onOpenChange={setShowInviteInstructorDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Send className="w-5 h-5 text-emerald-500" />
                Invite New Instructor
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800">
                  Send an invitation email to a new instructor. They'll receive a link to set up their account and access their courses.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Name *</label>
                  <Input
                    placeholder="Full name"
                    value={inviteInstructorForm.name}
                    onChange={(e) => setInviteInstructorForm({ ...inviteInstructorForm, name: e.target.value })}
                    data-testid="invite-instructor-name"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Email *</label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={inviteInstructorForm.email}
                    onChange={(e) => setInviteInstructorForm({ ...inviteInstructorForm, email: e.target.value })}
                    data-testid="invite-instructor-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Phone (optional)</label>
                  <Input
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={inviteInstructorForm.phone}
                    onChange={(e) => setInviteInstructorForm({ ...inviteInstructorForm, phone: e.target.value })}
                    data-testid="invite-instructor-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Bio (optional)</label>
                  <Textarea
                    placeholder="Brief bio or introduction..."
                    value={inviteInstructorForm.bio}
                    onChange={(e) => setInviteInstructorForm({ ...inviteInstructorForm, bio: e.target.value })}
                    className="h-24"
                    data-testid="invite-instructor-bio"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowInviteInstructorDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleInviteNewInstructor}
                  disabled={sendingInvite}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="send-invite-btn"
                >
                  {sendingInvite ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invite Sponsor Dialog */}
        <Dialog open={showInviteSponsorDialog} onOpenChange={setShowInviteSponsorDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Building className="w-5 h-5 text-emerald-500" />
                Invite Sponsor
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800">
                  Send an invitation email to a potential sponsor. They'll receive a link to complete their profile and apply for sponsorship.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Company Name *</label>
                  <Input
                    placeholder="Company name"
                    value={inviteSponsorForm.company_name}
                    onChange={(e) => setInviteSponsorForm({ ...inviteSponsorForm, company_name: e.target.value })}
                    data-testid="invite-sponsor-company"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Contact Name *</label>
                  <Input
                    placeholder="Contact person's name"
                    value={inviteSponsorForm.contact_name}
                    onChange={(e) => setInviteSponsorForm({ ...inviteSponsorForm, contact_name: e.target.value })}
                    data-testid="invite-sponsor-contact"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Email *</label>
                  <Input
                    type="email"
                    placeholder="email@company.com"
                    value={inviteSponsorForm.email}
                    onChange={(e) => setInviteSponsorForm({ ...inviteSponsorForm, email: e.target.value })}
                    data-testid="invite-sponsor-email"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Phone (optional)</label>
                  <Input
                    type="tel"
                    placeholder="(555) 555-5555"
                    value={inviteSponsorForm.phone}
                    onChange={(e) => setInviteSponsorForm({ ...inviteSponsorForm, phone: e.target.value })}
                    data-testid="invite-sponsor-phone"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Personal Message (optional)</label>
                  <Textarea
                    placeholder="Add a personal message to include in the invitation email..."
                    value={inviteSponsorForm.message}
                    onChange={(e) => setInviteSponsorForm({ ...inviteSponsorForm, message: e.target.value })}
                    className="h-20"
                    data-testid="invite-sponsor-message"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowInviteSponsorDialog(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleInviteSponsor}
                  disabled={sendingSponsorInvite}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="send-sponsor-invite-btn"
                >
                  {sendingSponsorInvite ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
